/** How a run's state reads in a list.
 *
 * Structurally typed rather than tied to `StatefulRun`, because the same rows
 * arrive through two hooks with two shapes: a project's own list
 * (`useStatefulRuns` -> `StatefulRun`) and the agent-level one
 * (`useAgentRuns` -> `AgentRun`, which carries no draft or completion fields).
 * Both list surfaces must say the same thing about the same run, so the wording
 * lives here and a payload that omits a field simply falls through.
 */
import { formatDurationSeconds } from "./formatDuration";

export interface RunStatusFacts {
  status: string;
  /** A scheduled pass still being worked: it holds no turn pointer. */
  pass_running?: boolean;
  active_turn_operation_key?: string | null;
  has_draft?: boolean;
}

/** ``end()`` can leave a pointer behind, so it means nothing on an ended run. */
export const runIsWorking = (run: RunStatusFacts): boolean =>
  !!run.pass_running || (!!run.active_turn_operation_key && run.status !== "ended");

export const runStatusLabel = (run: RunStatusFacts): string => {
  // Before the status: a re-opened pass is "interactive" yet can be mid-turn.
  if (runIsWorking(run)) return "Working…";
  if (run.status !== "active") return "Completed";
  return run.has_draft ? "Open (changes pending)" : "Open";
};

/** How long a scheduled pass took, or has taken so far while it runs. Null on a
 * conversation, and on a finished pass whose end was never recorded.
 *
 * Read from `pass_ended_at`, not `ended_at`: an operator re-opening the run to
 * ask about it moves `ended_at`, and that conversation is not the pass's work. */
export const passRunTimeLabel = (
  run: {
    kind: string;
    created_at: string;
    pass_ended_at?: string | null;
    pass_running?: boolean;
  },
  nowMs: number,
): string | null => {
  if (run.kind !== "scheduled") return null;
  const started = Date.parse(run.created_at);
  if (run.pass_running) return `${formatDurationSeconds((nowMs - started) / 1000)} so far`;
  if (!run.pass_ended_at) return null;
  return formatDurationSeconds((Date.parse(run.pass_ended_at) - started) / 1000);
};
